/** Pure formatting for the resume card and the employment timeline. */
import type { EmploymentEntry, ResumeParseStatus } from '@/features/pipeline/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Parser dates arrive as `YYYY-MM`, `YYYY-MM-DD`, `YYYY` or free text. */
export function formatResumeDate(s: string | null | undefined): string {
  if (!s) return '';
  const ym = /^(\d{4})-(\d{2})/.exec(s);
  if (ym) {
    const m = Number(ym[2]);
    return m >= 1 && m <= 12 ? `${MONTHS[m - 1]} ${ym[1]}` : ym[1]!;
  }
  if (/^\d{4}$/.test(s)) return s;
  return s;
}

function toMonths(s: string | null | undefined): number | null {
  if (!s) return null;
  const ym = /^(\d{4})(?:-(\d{2}))?/.exec(s);
  if (!ym) return null;
  return Number(ym[1]) * 12 + (ym[2] ? Number(ym[2]) - 1 : 0);
}

/** "2 yrs 3 mos" for a job; empty when the dates are not machine-readable. */
export function tenureLabel(e: Pick<EmploymentEntry, 'start' | 'end' | 'is_current'>, now = new Date()): string {
  const a = toMonths(e.start);
  const b = e.is_current && !e.end ? now.getFullYear() * 12 + now.getMonth() : toMonths(e.end);
  if (a === null || b === null || b < a) return '';
  const months = b - a + 1;
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [];
  if (y) parts.push(`${y} yr${y === 1 ? '' : 's'}`);
  if (m) parts.push(`${m} mo${m === 1 ? '' : 's'}`);
  return parts.join(' ') || '1 mo';
}

export function dateRange(e: Pick<EmploymentEntry, 'start' | 'end' | 'is_current'>): string {
  const start = formatResumeDate(e.start);
  const end = e.is_current && !e.end ? 'Present' : formatResumeDate(e.end);
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
}

export function yearsLabel(n: number | null): string {
  if (n === null) return '';
  const r = Math.round(n * 10) / 10;
  return `${r} yr${r === 1 ? '' : 's'}`;
}

export interface ParseStatusMeta {
  label: string;
  /** Tailwind classes for the chip. */
  chip: string;
  /** Explanation shown under the chip when the state needs one. */
  hint: string | null;
}

export function parseStatusMeta(status: ResumeParseStatus, error?: string | null): ParseStatusMeta {
  switch (status) {
    case 'pending':
      return { label: 'Reading…', chip: 'bg-info-blue-bg text-info-blue', hint: 'Pulling out work history, skills and certifications.' };
    case 'parsed':
      return { label: 'Parsed', chip: 'bg-success-bg text-success', hint: null };
    case 'failed':
      return { label: 'Could not read', chip: 'bg-danger-bg text-danger', hint: error || 'The parser failed. Try "Re-parse", or upload a cleaner PDF.' };
    case 'unsupported':
      // Dark text on the amber tint: `text-warning` on `bg-warning-bg` is 3.45:1, under AA for 12 px (QA stage 3).
      return { label: 'Not readable', chip: 'bg-warning-bg text-gray-800', hint: error || 'This format cannot be parsed. Upload a PDF or .docx to extract work history.' };
    default:
      return { label: 'Not parsed', chip: 'bg-page text-gray-700', hint: null };
  }
}
